import * as THREE from 'three';
import { preparationFrame } from './idle-preparation.js';
import { markPerformance } from '../performance-mark.js';

/** Shader compilation that never finishes (a lost context, a hidden tab) must not hold the player at the loading screen. */
const COMPILE_TIMEOUT_MS = 30_000;
/**
 * Upload batches start this small and double while a draw stays under `UPLOAD_DRAW_MS` (halving when one takes twice that), up to
 * `UPLOAD_BATCH_MOST`: a map holds about 10,000 drawables, which fixed batches of 32 spread over 300 frames, each drawing the whole scene's shadows.
 */
const UPLOAD_BATCH_SIZE = 32, UPLOAD_BATCH_MOST = 1024, UPLOAD_DRAW_MS = 12;

export type Drawable = THREE.Mesh | THREE.Line | THREE.Points | THREE.Sprite;
const isDrawable = (object: THREE.Object3D): object is Drawable => object instanceof THREE.Mesh || object instanceof THREE.Line || object instanceof THREE.Points || object instanceof THREE.Sprite;

/** Compile existing objects without reparenting them or including their descendants. */
export function compileObjects(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera, objects: readonly Drawable[]) {
  const batch = new THREE.Group();
  batch.traverse = callback => { callback(batch); for (const object of objects) callback(object); };
  return renderer.compileAsync(batch, camera, scene);
}

export interface WarmSceneOptions {
  frame?: () => Promise<void>;
  prepare?: (objects: readonly Drawable[]) => Promise<void>;
  finish?: () => Promise<void>;
  /** Warming's own share done, 0–1: shader programs finished linking, then upload batches drawn. */
  progress?: (fraction: number) => void;
  /**
   * Objects an earlier warming on this renderer and context already drew: their buffers and textures are uploaded, so they are compiled
   * again (lights or fog may have changed their programs) but not drawn again. Each object drawn here joins it.
   */
  uploaded?: WeakSet<THREE.Object3D>;
}

/** How much of warming is shader compilation; the rest is the upload batches. */
const COMPILE_SHARE = .7;
/** Of the compile share, how much is creating programs (synchronous, batch by batch); the rest is the driver linking them. */
const CREATE_SHARE = .6;
const COMPILE_BATCH_SIZE = 48;
const PROGRESS_POLL_MS = 50;

/** Programs three.js has finished linking; `isReady` is the same non-blocking query `compileAsync` polls. */
function readyPrograms(renderer: THREE.WebGLRenderer) {
  const programs = (renderer.info?.programs ?? []) as unknown as { isReady?: () => boolean }[];
  let ready = 0;
  for (const program of programs) if (program.isReady?.() ?? true) ready++;
  return { ready, total: programs.length };
}

/**
 * Upload even fog-hidden/off-screen scenery before the first interactive frame.
 *
 * Warm batches draw to the canvas itself, behind the loading overlay: three.js keys its programs by
 * output colour space and tone mapping, which differ for offscreen targets, so a target render would compile
 * (synchronously) a second set of shaders the game never draws with.
 */
export async function warmScene(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera, active = () => true, timeoutMs = COMPILE_TIMEOUT_MS, options: WarmSceneOptions = {}) {
  const objects: { object: THREE.Object3D; visible: boolean; culled: boolean; layers: number }[] = [];
  const drawables: Drawable[] = [];
  const frame = options.frame ?? preparationFrame;
  const progress = options.progress ?? (() => {});
  let timer: ReturnType<typeof setTimeout> | undefined, polling = true;
  try {
    const reveal = (object: THREE.Object3D) => {
      objects.push({ object, visible: object.visible, culled: object.frustumCulled, layers: object.layers.mask });
      if (!(object instanceof THREE.Light)) object.visible = true;
      object.frustumCulled = false;
      if (isDrawable(object)) drawables.push(object);
      // Skeleton-only test rigs never draw: revealing them would render unnecessary prop geometry.
      for (const child of object.children) if (!child.userData.nimblezSkipWarm) reveal(child);
    };
    reveal(scene);
    // Programs are created a batch at a time, yielding between batches so the loading bar can paint; parallel
    // compilation (KHR_parallel_shader_compile) still overlaps the driver's linking across all of them, and stragglers finish lazily.
    const before = readyPrograms(renderer).ready, linking: Promise<unknown>[] = [];
    const created = (done: number) => CREATE_SHARE * done / Math.max(1, drawables.length);
    const compiled = (async () => {
      for (let offset = 0; offset < Math.max(1, drawables.length); offset += COMPILE_BATCH_SIZE) {
        const batch = compileObjects(renderer, scene, camera, drawables.slice(offset, offset + COMPILE_BATCH_SIZE));
        batch.catch(() => {}); // reported once, through `Promise.all` below
        linking.push(batch);
        progress(COMPILE_SHARE * created(Math.min(drawables.length, offset + COMPILE_BATCH_SIZE)));
        await new Promise(resolve => setTimeout(resolve, 0));
        if (!active()) return;
      }
      // Linking: count the new programs as the driver finishes them.
      void (async () => {
        while (polling) {
          const { ready, total } = readyPrograms(renderer);
          if (total > before) progress(COMPILE_SHARE * (CREATE_SHARE + (1 - CREATE_SHARE) * (ready - before) / (total - before)));
          await new Promise(resolve => setTimeout(resolve, PROGRESS_POLL_MS));
        }
      })();
      await Promise.all(linking);
    })();
    await Promise.race([compiled, new Promise<void>(resolve => { timer = setTimeout(resolve, timeoutMs); })]);
    polling = false; clearTimeout(timer);
    if (!active()) return;
    progress(COMPILE_SHARE);
    markPerformance('nimblez:warm-compiled');
    await options.prepare?.(drawables);
    if (!active()) return;
    // Layers hide individual draw calls without hiding descendant lights or changing the scene's shader variants.
    // Each batch also initializes its shadow programs and buffers. The next ordinary frame redraws complete shadows.
    for (const object of drawables) object.layers.mask = 0;
    const uploading = options.uploaded ? drawables.filter(object => !options.uploaded!.has(object)) : drawables;
    let offset = 0, size = UPLOAD_BATCH_SIZE;
    do {
      await frame();
      if (!active()) return;
      const batch = uploading.slice(offset, offset + size), began = performance.now();
      for (const object of batch) object.layers.mask = camera.layers.mask;
      renderer.render(scene, camera);
      for (const object of batch) { object.layers.mask = 0; options.uploaded?.add(object); }
      const spent = performance.now() - began;
      offset += size;
      size = spent < UPLOAD_DRAW_MS ? Math.min(UPLOAD_BATCH_MOST, size * 2) : spent > 2 * UPLOAD_DRAW_MS ? Math.max(UPLOAD_BATCH_SIZE, size / 2) : size;
      progress(COMPILE_SHARE + (1 - COMPILE_SHARE) * Math.min(1, offset / Math.max(1, uploading.length)));
    } while (offset < uploading.length);
    if (active()) await options.finish?.();
    markPerformance('nimblez:warm-drawn');
  } finally {
    polling = false; clearTimeout(timer);
    for (const { object, visible, culled, layers } of objects) { object.visible = visible; object.frustumCulled = culled; object.layers.mask = layers; }
  }
}
